import React, { useMemo } from 'react';
import styles from './GearCategoryView.module.css';
import GearCategorySection from './GearCategorySection';
import { useTranslation } from 'react-i18next';

function GearCategoryView({ items, categoryLabel, categoryId, actions }) {
  const { t } = useTranslation();

  const groupedItems = useMemo(() => {
    if (items.length === 0) return {};

    const groups = {};

    if (categoryId === 'boots') {
      groups['Hiking boots'] = { items: [], type: 'boots' };
      groups['B1'] = { items: [], type: 'boots' };
      groups['B2'] = { items: [], type: 'boots' };
      groups['B3'] = { items: [], type: 'boots' };
      
      items.forEach(item => {
        const compat = (item.cramponCompat || '').toUpperCase();
        if (['B1', 'B2', 'B3'].includes(compat)) {
          groups[compat].items.push(item);
        } else {
          groups['Hiking boots'].items.push(item);
        }
      });
    } else if (categoryId === 'crampons_axes') {
      groups['Ice axes'] = { items: [], type: 'ice_axes' };
      groups['Strap-on'] = { items: [], type: 'crampons' };
      groups['Semi-automatic'] = { items: [], type: 'crampons' };
      groups['Automatic'] = { items: [], type: 'crampons' };
      groups['Hybrid'] = { items: [], type: 'crampons' };
      groups['Other Crampons'] = { items: [], type: 'crampons' };

      const mapCramponCat = (raw) => {
        if (!raw) return '';
        const s = String(raw).trim().toLowerCase();
        // Legacy C-ratings mapping
        if (s === 'c0') return 'Hybrid';
        if (s === 'c1') return 'Strap-on';
        if (s === 'c2') return 'Semi-automatic';
        if (s === 'c3') return 'Automatic';
        // Accept already human-readable values
        if (s.includes('strap')) return 'Strap-on';
        if (s.includes('semi')) return 'Semi-automatic';
        if (s.includes('auto')) return 'Automatic';
        return '';
      };

      items.forEach(item => {
        if (item.category === 'Ice axes') {
          groups['Ice axes'].items.push(item);
        } else if (item.category === 'Crampons' || item.category === 'Crampon') {
          const mapped = mapCramponCat(item.cramponCat);
          if (mapped && groups[mapped]) {
            groups[mapped].items.push(item);
          } else {
            groups['Other Crampons'].items.push(item);
          }
        }
      });
    } else if (categoryId === 'backpacks') {
      // Group by liters
      groups['Small (<25L)'] = { items: [], type: 'backpacks' };
      groups['Medium (25-40L)'] = { items: [], type: 'backpacks' };
      groups['Large (40L+)'] = { items: [], type: 'backpacks' };
      groups['Unspecified'] = { items: [], type: 'backpacks' };

      items.forEach(item => {
        const l = parseFloat(item.liters);
        if (!Number.isFinite(l)) {
          groups['Unspecified'].items.push(item);
        } else if (l < 25) {
          groups['Small (<25L)'].items.push(item);
        } else if (l <= 40) {
          groups['Medium (25-40L)'].items.push(item);
        } else {
          groups['Large (40L+)'].items.push(item);
        }
      });
    } else if (categoryId === 'jackets') {
      groups['Soft shell'] = { items: [], type: 'clothing' };
      groups['Hard shell'] = { items: [], type: 'clothing' };
      groups['Insulated'] = { items: [], type: 'clothing' };
      groups['Wind breaker'] = { items: [], type: 'clothing' };
      groups['Other'] = { items: [], type: 'clothing' };

      items.forEach(item => {
        const sub = item.subCategory;
        if (sub && groups[sub]) {
          groups[sub].items.push(item);
        } else {
          groups['Other'].items.push(item);
        }
      });
    } else if (categoryId === 'pants') {
      groups['Soft shell'] = { items: [], type: 'clothing' };
      groups['Hard shell'] = { items: [], type: 'clothing' };
      groups['Trekking'] = { items: [], type: 'clothing' };
      groups['Other'] = { items: [], type: 'clothing' };

      items.forEach(item => {
        const sub = item.subCategory;
        if (sub && groups[sub]) {
          groups[sub].items.push(item);
        } else {
          groups['Other'].items.push(item);
        }
      });
    } else if (categoryId === 'baselayers') {
      groups['Top'] = { items: [], type: 'clothing' };
      groups['Bottom'] = { items: [], type: 'clothing' };
      groups['Other'] = { items: [], type: 'clothing' };

      items.forEach(item => {
        const sub = item.subCategory;
        if (sub && groups[sub]) {
          groups[sub].items.push(item);
        } else {
          groups['Other'].items.push(item);
        }
      });
    } else if (categoryId === 'hardshell' || categoryId === 'softshell' || categoryId === 'midlayer') {
      // Simple list for these categories - no subcategories
      groups['All'] = { items: items, type: 'clothing' };
    } else {
      // Default no grouping
      groups['All'] = { items: items, type: 'other' };
    }

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].items.length === 0) delete groups[key];
    });

    return groups;
  }, [items, categoryId]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{categoryLabel}</h2>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>

      {Object.keys(groupedItems).length > 0 ? (
        Object.entries(groupedItems).map(([groupName, groupData]) => (
          <GearCategorySection 
            key={groupName}
            title={groupName} 
            items={groupData.items}
            categoryType={groupData.type}
          />
        ))
      ) : (
        <div className={styles.noGear}>
          {t('gear.noGear')}
        </div>
      )}
    </div>
  );
}

export default GearCategoryView;
